/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { startOfDay, differenceInDays } from 'date-fns';
import memoizeOne, { type MemoizedFn } from 'memoize-one';
import type { ValueDecoration } from '@jira/polaris__domain-field/src/ValueDecoration.types.tsx';
import type { IntervalValue } from '@jira/polaris__lib-date-time/src/types.tsx';
import { applyOptionLogic } from './applyOptionLogic.tsx';
import { applyDateTimeIntervalLogic, applyDateTimeLogic } from './applyDateTimeIntervalLogic.tsx';
import { applyNumberLogic } from './createDefaultLogic.tsx';

/**
 * For selects and labels fields.
 */
export const getAppliedDecoration: MemoizedFn<
	(decorations?: ValueDecoration[], value?: string) => ValueDecoration | undefined
> = memoizeOne((decorations?: ValueDecoration[], value?: string): ValueDecoration | undefined => {
	if (decorations !== undefined && value !== undefined) {
		return decorations.find((decoration: ValueDecoration) =>
			applyOptionLogic(decoration.logic, value),
		);
	}
	return undefined;
});

/**
 * For selects and labels fields.
 */
export const getAppliedDecorations: MemoizedFn<
	(decorations: ValueDecoration[], values: string[]) => Record<string, ValueDecoration | undefined>
> = memoizeOne(
	(
		decorations: ValueDecoration[],
		values: string[],
	): Record<string, ValueDecoration | undefined> => {
		const decorationMap: Record<string, ValueDecoration | undefined> = {};

		for (const value of values) {
			decorationMap[value] = getAppliedDecoration(decorations, value);
		}

		return decorationMap;
	},
);

export const getAppliedNumberDecoration = (
	decorations?: ValueDecoration[],
	value?: number,
): ValueDecoration | undefined => {
	if (decorations !== undefined && value !== undefined) {
		return decorations.find((decoration: ValueDecoration) =>
			applyNumberLogic(decoration.logic, value),
		);
	}
	return undefined;
};

export const getAppliedDateDecoration: MemoizedFn<
	(
		decorations: ValueDecoration[] | undefined,
		value: string | undefined,
	) => ValueDecoration | undefined
> = memoizeOne(
	(
		decorations: ValueDecoration[] | undefined,
		value: string | undefined,
	): ValueDecoration | undefined => {
		if (decorations !== undefined && value !== undefined) {
			const given = startOfDay(new Date(value));
			const current = startOfDay(new Date());

			const diffDays = differenceInDays(given, current);

			return decorations.find((decoration: ValueDecoration) =>
				applyDateTimeLogic(decoration.logic, diffDays),
			);
		}
		return undefined;
	},
);

export const getAppliedIntervalDecoration: MemoizedFn<
	(
		decorations: ValueDecoration[] | undefined,
		value: IntervalValue | undefined,
	) => ValueDecoration | undefined
> = memoizeOne(
	(
		decorations: ValueDecoration[] | undefined,
		value: IntervalValue | undefined,
	): ValueDecoration | undefined => {
		if (decorations !== undefined && value !== undefined) {
			const startDate = startOfDay(new Date(value?.start));
			const endDate = startOfDay(new Date(value?.end));
			const current = startOfDay(new Date());

			const diffDaysBeforeStart = differenceInDays(startDate, current);
			const diffDaysBeforeEnd = differenceInDays(endDate, current);

			return decorations.find((decoration: ValueDecoration) =>
				applyDateTimeIntervalLogic({
					logic: decoration.logic,
					diffDaysBeforeStart,
					diffDaysBeforeEnd,
				}),
			);
		}
		return undefined;
	},
);
